import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  SimpleGrid,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Icon,
  Divider,
  Card,
  CardHeader,
  CardBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Progress,
  Button,
  Flex,
  Tooltip,
  useToast,
  Switch,
  FormControl,
  FormLabel,
  Input,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Select,
  Checkbox
} from '@chakra-ui/react';
import { 
  TrendingUp, 
  TrendingDown, 
  Clock, 
  Target, 
  Shield, 
  Gem,
  Building2,
  Coins,
  DollarSign,
  Calendar,
  Brain,
  BarChart3,
  Wallet,
  RefreshCw,
  Zap,
  Play,
  Pause,
  Settings,
  TrendingUpIcon,
  BarChart2,
  Activity,
  Download,
  AlertTriangle,
  ExternalLink
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip as RechartsTooltip, 
  Legend, 
  ResponsiveContainer,
  Area,
  AreaChart,
  ComposedChart,
  ReferenceLine
} from 'recharts';
import { apiClient } from '../services/apiClient';

// Import help components
import KnowledgeBase from './KnowledgeBase';
import OnboardingTutorial from './OnboardingTutorial';
import HelpCenter from './HelpCenter';
import GeopoliticalSentimentIndex from './GeopoliticalSentimentIndex';
import GeopoliticalAlertSystem from './GeopoliticalAlertSystem';

// MetaMask Integration
declare global {
  interface Window {
    ethereum?: any;
  }
}

interface RWASignal {
  asset: string;
  horizon: 'Daily' | 'Weekly' | 'Monthly' | 'Long-Term';
  signal: 'BUY' | 'SELL' | 'HOLD';
  entry: number;
  target: number;
  stopLoss: number;
  riskReward: number;
  confidence: number;
  expectedMove: number;
  narrative: string;
  timestamp: string;
  expectedROI?: string;
}

interface RWAMetrics {
  totalTVL: string;
  monthlyChange: string;
  topProtocols: Array<{
    name: string;
    tvl: string;
    change: string;
  }>;
  catalysts: string[];
  timestamp: string;
}

interface RWASummary {
  rwaTvl: string;
  avgConfidence: number;
  topCatalyst: string;
  timestamp: string;
}

interface RWAData {
  summary: RWASummary;
  signals: RWASignal[];
  metrics: RWAMetrics;
}

interface AutoTradeConfig {
  isEnabled: boolean;
  confidenceThreshold: number;
  portfolioSize: number;
  maxPositionSize: number;
  maxTradesPerDay: number;
  dailyTradeCount: number;
  activePositions: any[];
  tradeHistory: any[];
}

interface WalletState {
  isConnected: boolean;
  address: string;
  balance: string;
}

interface BacktestMetrics {
  total_return: number;
  max_drawdown: number;
  sharpe_ratio: number;
  win_rate: number;
  total_trades: number;
  high_conf_total_return: number;
  high_conf_max_drawdown: number;
  high_conf_sharpe_ratio: number;
  high_conf_win_rate: number;
  high_conf_trades: number;
  period_days: number;
  sma_window: number;
  confidence_threshold: number;
}

interface EquityPoint {
  date: string;
  value: number;
  high_conf_value: number;
}

interface BacktestResult {
  success: boolean;
  metrics: BacktestMetrics;
  equity_curve: EquityPoint[];
  asset_performance: Record<string, any>;
  summary: any;
}

interface BacktestParams {
  assets: string[];
  start_date: string;
  end_date: string;
  sma_window: number;
  confidence_threshold: number;
  // Enhanced parameters
  strategy_type?: string;
  initial_capital?: number;
  sma_short?: number;
  sma_long?: number;
  high_conf_threshold?: number;
  benchmark?: string;
  monte_carlo?: boolean;
  monte_carlo_sims?: number;
  volume_filter?: number;
  rsi_buy?: number;
  rsi_sell?: number;
  catalyst_dates?: string[];
}

interface RWAPlaybookProps {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

export const RWAPlaybook: React.FC<RWAPlaybookProps> = ({
  autoRefresh = true,
  refreshInterval = 120000, // 2 minutes default (less frequent for fundamental data)
}) => {
  // Feature toggles (set to false to hide for later use)
  const SHOW_ALERT_SYSTEM = false; // Set to true to enable GeopoliticalAlertSystem
  
  const [data, setData] = useState<RWAData | null>(null);
  const [autoTradeConfig, setAutoTradeConfig] = useState<AutoTradeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedHorizon, setSelectedHorizon] = useState<string>('all');
  const [executingTrade, setExecutingTrade] = useState<string | null>(null);
  
  // Backtest state
  const [backtestResult, setBacktestResult] = useState<BacktestResult | null>(null);
  const [backtestLoading, setBacktestLoading] = useState(false);
  const [backtestParams, setBacktestParams] = useState<BacktestParams>({
    assets: ['ONDO', 'CFG', 'VBILL', 'BLK', 'ETH'],
    start_date: '2023-01-01',
    end_date: '2024-12-31',
    sma_window: 50,
    confidence_threshold: 0.85,
    // Enhanced defaults
    strategy_type: 'sma_momentum',
    initial_capital: 10000,
    sma_short: 10,
    sma_long: 50,
    high_conf_threshold: 2.0,
    benchmark: 'SPY',
    monte_carlo: false,
    monte_carlo_sims: 100,
    volume_filter: 1.5,
    rsi_buy: 60,
    rsi_sell: 40,
    catalyst_dates: []
  });
  
  const toast = useToast();
  const { isOpen: isMainModalOpen, onOpen: onMainModalOpen, onClose: onMainModalClose } = useDisclosure();
  const { 
    isOpen: isBacktestOpen, 
    onOpen: onBacktestOpen, 
    onClose: onBacktestClose 
  } = useDisclosure();
  
  // Help system state
  const {
    isOpen: isKnowledgeBaseOpen,
    onOpen: onKnowledgeBaseOpen,
    onClose: onKnowledgeBaseClose
  } = useDisclosure();
  
  const {
    isOpen: isTutorialOpen,
    onOpen: onTutorialOpen,
    onClose: onTutorialClose
  } = useDisclosure();
  
  const [isFirstVisit, setIsFirstVisit] = useState<boolean>(false);
  const [hasCompletedTutorial, setHasCompletedTutorial] = useState<boolean>(false);
  
  // Wallet state
  const [walletState, setWalletState] = useState<WalletState>({
    isConnected: false,
    address: '',
    balance: '0',
  });

  const fetchRWASignals = async (showRefreshToast = false) => {
    try {
      if (showRefreshToast) {
        setRefreshing(true);
      } else if (!data) {
        setLoading(true);
      }
      
      console.log('🔍 Fetching RWA signals from API...');
      
      // Fetch from API endpoint - apiClient now auto-unwraps the response
      const rwaData = await apiClient.getRWASignals();
      
      console.log('📊 RWA data received:', rwaData);
      
      if (!rwaData || typeof rwaData !== 'object') {
        throw new Error('Invalid RWA data format received');
      }
      
      // Validate data has required fields
      const hasSignals = Array.isArray(rwaData.signals);
      const hasCatalysts = rwaData.catalysts && typeof rwaData.catalysts === 'object';
      const hasSummary = rwaData.summary && typeof rwaData.summary === 'object';
      const hasMetrics = rwaData.metrics && typeof rwaData.metrics === 'object';
      
      if (!hasSignals && !hasCatalysts && !hasSummary && !hasMetrics) {
        console.warn('⚠️ RWA data missing required fields. Received:', Object.keys(rwaData));
        // Provide default structure for missing data
        setData({
          summary: rwaData.summary || { rwaTvl: '$0', avgConfidence: 0, topCatalyst: 'Pending data', timestamp: new Date().toISOString() },
          signals: rwaData.signals || [],
          metrics: rwaData.metrics || { totalTVL: '$0', monthlyChange: '$0', topProtocols: [], catalysts: [], timestamp: new Date().toISOString() }
        });
      } else {
        setData(rwaData);
      }
      
      setLastUpdate(new Date());
      setError(null);
      console.log(`✅ RWA data loaded successfully`);

      
      if (showRefreshToast) {
        toast({
          title: '🔄 RWA signals updated',
          description: `Latest data from ${new Date().toLocaleTimeString()}`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
    } catch (err) {
      console.error('❌ Full error details:', err);
      
      const isNetworkError = err instanceof TypeError && err.message.includes('fetch');
      const errorMessage = isNetworkError 
        ? 'Network error: API server may be down. Please check if the backend is running on port 3001.'
        : err instanceof Error ? err.message : 'Unknown error occurred';
        
      setError(errorMessage);
      console.error('Error fetching RWA signals:', err);
      
      if (showRefreshToast) {
        toast({
          title: '❌ Connection Failed',
          description: isNetworkError ? 'Check API server status' : errorMessage,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchRWASignals();
    fetchAutoTradeConfig();
    
    // Check if first visit
    const hasVisited = localStorage.getItem('alpha-playbook-visited');
    const tutorialCompleted = localStorage.getItem('alpha-playbook-tutorial-completed');
    
    if (!hasVisited) {
      setIsFirstVisit(true);
      setTimeout(() => onTutorialOpen(), 1000); // Delay to let UI load
      localStorage.setItem('alpha-playbook-visited', 'true');
    }
    
    if (tutorialCompleted) {
      setHasCompletedTutorial(true);
    }

    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchRWASignals();
        fetchAutoTradeConfig();
      }, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]); // Remove onTutorialOpen from deps
  
  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === '?') {
        event.preventDefault();
        onKnowledgeBaseOpen();
      }
      if (event.ctrlKey && event.key === 't') {
        event.preventDefault();
        onTutorialOpen();
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []); // Empty deps array is fine for event listeners

  const fetchAutoTradeConfig = async () => {
    try {
      const result = await apiClient.getAutoTradeConfig();
      if (result.success) {
        setAutoTradeConfig(result.data);
      }
    } catch (error) {
      console.error('Error fetching auto-trade config:', error);
    }
  };

  const updateAutoTradeConfig = async (updates: Partial<AutoTradeConfig>) => {
    try {
      const result = await apiClient.updateAutoTradeConfig(updates);
      if (result.success) {
        setAutoTradeConfig(result.data);
        toast({
          title: '🔄 Auto-Trade Updated',
          description: 'Configuration saved successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: '❌ Update Failed',
        description: 'Could not update auto-trade settings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const executeManualTrade = async (signal: RWASignal) => {
    try {
      setExecutingTrade(signal.asset);
      
      const result = await apiClient.executeManualTrade(signal);
      
      if (result.success) {
        toast({
          title: `🎯 Trade Executed!`,
          description: `${signal.signal} ${signal.asset} - Manual execution successful`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        // Refresh auto-trade config to show new position
        fetchAutoTradeConfig();
      }
    } catch (error) {
      toast({
        title: '❌ Trade Failed',
        description: `Could not execute ${signal.signal} ${signal.asset}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setExecutingTrade(null);
    }
  };

  const runBacktest = async () => {
    try {
      setBacktestLoading(true);
      console.log('� Running enhanced backtest with params:', backtestParams);
      
      // Convert old parameter names to new API format
      const apiParams = {
        symbols: backtestParams.assets,
        strategy_type: backtestParams.strategy_type || 'sma_momentum',
        start_date: backtestParams.start_date,
        end_date: backtestParams.end_date,
        initial_capital: backtestParams.initial_capital || 10000,
        sma_short: backtestParams.sma_short || 10,
        sma_long: backtestParams.sma_long || 50,
        high_conf_threshold: backtestParams.high_conf_threshold || backtestParams.confidence_threshold,
        benchmark: backtestParams.benchmark || 'SPY',
        monte_carlo: backtestParams.monte_carlo || false,
        monte_carlo_sims: backtestParams.monte_carlo_sims || 100,
        volume_filter: backtestParams.volume_filter || 1.5,
        rsi_buy: backtestParams.rsi_buy || 60,
        rsi_sell: backtestParams.rsi_sell || 40,
        catalyst_dates: backtestParams.catalyst_dates || []
      };
      
      console.log('📡 Sending to enhanced backtest API:', apiParams);
      
      const response = await fetch('http://localhost:3001/api/backtest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiParams),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }
      
      const result = await response.json();
      
      setBacktestResult(result);
      toast({
        title: '🎯 Enhanced Backtest Completed',
        description: `${result.summary?.strategy || 'Strategy'} analyzed • Return: ${formatPercentage(result.metrics?.total_return || 0)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      
      if (result.monte_carlo) {
        toast({
          title: '🎲 Monte Carlo Analysis',
          description: `${result.monte_carlo.simulations} simulations • Win rate: ${formatPercentage(result.monte_carlo.win_probability || 0)}`,
          status: 'info',
          duration: 4000,
          isClosable: true,
        });
      }
      
    } catch (error) {
      console.error('❌ Enhanced backtest error:', error);
      toast({
        title: '❌ Backtest Error',
        description: error instanceof Error ? error.message : 'Failed to run backtest',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setBacktestResult(null);
    } finally {
      setBacktestLoading(false);
    }
  };

  const formatPercentage = (value: number): string => {
    return `${(value * 100).toFixed(2)}%`;
  };

  // MetaMask wallet functions
  const connectWallet = async () => {
    if (!window.ethereum) {
      toast({
        title: 'MetaMask Not Found',
        description: 'Please install MetaMask to connect your wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      const accounts = await window.ethereum.request({ 
        method: 'eth_requestAccounts' 
      });
      
      const address = accounts[0];
      const balanceWei = await window.ethereum.request({
        method: 'eth_getBalance',
        params: [address, 'latest'],
      });
      
      const balanceEth = (parseInt(balanceWei, 16) / 1e18).toFixed(4);
      
      setWalletState({
        isConnected: true,
        address,
        balance: balanceEth,
      });

      toast({
        title: 'Wallet Connected',
        description: `Connected: ${address.substring(0, 6)}...${address.substring(38)}`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: 'Connection Failed',
        description: err instanceof Error ? err.message : 'Failed to connect wallet',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const disconnectWallet = () => {
    setWalletState({
      isConnected: false,
      address: '',
      balance: '0',
    });
    
    toast({
      title: 'Wallet Disconnected',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };
  
  const handleTutorialComplete = () => {
    setHasCompletedTutorial(true);
    localStorage.setItem('alpha-playbook-tutorial-completed', 'true');
    onTutorialClose();
    
    toast({
      title: '🎉 Tutorial Completed!',
      description: 'You\'re ready to start using Alpha Playbook. Access help anytime using the help button.',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };
  
  const handleOpenHelp = () => {
    onKnowledgeBaseOpen();
  };

  const filteredSignals = data?.signals.filter(signal => 
    selectedHorizon === 'all' || signal.horizon === selectedHorizon
  ) || [];

  const getHorizonColor = (horizon: string): string => {
    switch (horizon) {
      case 'Daily': return 'blue';
      case 'Weekly': return 'green';
      case 'Monthly': return 'purple';
      case 'Long-Term': return 'orange';
      default: return 'gray';
    }
  };

  const getSignalColor = (signal: string): string => {
    switch (signal) {
      case 'BUY': return 'green';
      case 'SELL': return 'red';
      case 'HOLD': return 'yellow';
      default: return 'gray';
    }
  };

  const getSignalIcon = (signal: string) => {
    switch (signal) {
      case 'BUY': return TrendingUp;
      case 'SELL': return TrendingDown;
      case 'HOLD': return Coins;
      default: return Coins;
    }
  };

  const handleManualRefresh = () => {
    fetchRWASignals(true);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" color="purple.500" thickness="4px" />
        <Text mt={4} color="gray.600">Loading RWA Playbook signals...</Text>
        <Text fontSize="sm" color="gray.500">Fetching tokenization alpha opportunities</Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        <Box flex="1">
          <AlertTitle>RWA Signal Loading Issue</AlertTitle>
          <AlertDescription>
            {error}
            <Button size="sm" ml={4} onClick={() => fetchRWASignals()} colorScheme="orange">
              Retry
            </Button>
          </AlertDescription>
        </Box>
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert status="info" borderRadius="lg">
        <AlertIcon />
        <AlertTitle>No RWA data available</AlertTitle>
        <AlertDescription>Please try again later.</AlertDescription>
      </Alert>
    );
  }

  return (
    <VStack spacing={6} align="stretch">
      {/* Header with Controls */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
        <VStack align="start" spacing={1}>
          <HStack>
            <Icon as={Gem} color="purple.500" boxSize={8} />
            <Heading size="lg" color="gray.800">
              💎 RWA Playbook
            </Heading>
          </HStack>
          <Text color="gray.600">Real-World Assets Tokenization Alpha Signals</Text>
          <HStack spacing={2} fontSize="sm" color="gray.500">
            <Icon as={Clock} boxSize={4} />
            <Text>
              Last updated: {lastUpdate?.toLocaleTimeString() || 'Never'}
            </Text>
            <Icon as={Brain} boxSize={4} ml={2} />
            <Text>Multi-horizon AI analysis</Text>
          </HStack>
        </VStack>

        <VStack spacing={3} align="end">
          {/* Auto-Trade Toggle */}
          <HStack spacing={4}>
            <VStack spacing={1} align="end">
              <HStack>
                <Icon as={autoTradeConfig?.isEnabled ? Play : Pause} 
                      color={autoTradeConfig?.isEnabled ? 'green.500' : 'gray.400'} 
                      boxSize={4} />
                <Text fontSize="sm" fontWeight="medium">Auto-Trade Bot</Text>
                <Switch
                  isChecked={autoTradeConfig?.isEnabled || false}
                  onChange={(e) => updateAutoTradeConfig({ isEnabled: e.target.checked })}
                  colorScheme="green"
                  size="md"
                />
              </HStack>
              <Text fontSize="xs" color={autoTradeConfig?.isEnabled ? 'green.600' : 'gray.500'}>
                {autoTradeConfig?.isEnabled ? 
                  `Active • ${autoTradeConfig.dailyTradeCount}/${autoTradeConfig.maxTradesPerDay} trades today` : 
                  'Disabled'
                }
              </Text>
            </VStack>
            
            <Button
              leftIcon={<Settings />}
              colorScheme="purple"
              variant="outline"
              onClick={onMainModalOpen}
              size="sm"
            >
              Settings
            </Button>
          </HStack>

          {/* Manual Refresh */}
          <Button
            leftIcon={<RefreshCw />}
            colorScheme="purple"
            variant="outline"
            onClick={handleManualRefresh}
            isLoading={refreshing}
            loadingText="Refreshing"
            size="sm"
          >
            Refresh Signals
          </Button>
          
          {/* Backtest Button */}
          <Button
            leftIcon={<BarChart3 />}
            colorScheme="blue"
            variant="outline"
            onClick={onBacktestOpen}
            size="sm"
          >
            Backtest Signals
          </Button>
        </VStack>
      </Flex>

      {/* RWA Market Overview */}
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
        <Stat bg="purple.50" p={4} borderRadius="lg" border="1px solid" borderColor="purple.200">
          <StatLabel>RWA Market TVL</StatLabel>
          <StatNumber color="purple.600">${data.summary.rwaTvl}</StatNumber>
          <StatHelpText>
            <HStack>
              <Icon as={TrendingUp} color="green.500" boxSize={4} />
              <Text>{data.metrics?.monthlyChange || '+0.0%'} MoM</Text>
            </HStack>
          </StatHelpText>
        </Stat>

        <Stat bg="green.50" p={4} borderRadius="lg" border="1px solid" borderColor="green.200">
          <StatLabel>Avg Confidence</StatLabel>
          <StatNumber color="green.600">{data.summary.avgConfidence}%</StatNumber>
          <StatHelpText>High conviction</StatHelpText>
        </Stat>

        <Stat bg="blue.50" p={4} borderRadius="lg" border="1px solid" borderColor="blue.200">
          <StatLabel>Active Signals</StatLabel>
          <StatNumber color="blue.600">{data.signals.length}</StatNumber>
          <StatHelpText>Multi-timeframe</StatHelpText>
        </Stat>

        <Stat bg="orange.50" p={4} borderRadius="lg" border="1px solid" borderColor="orange.200">
          <StatLabel>ROI Potential</StatLabel>
          <StatNumber color="orange.600">50-200%</StatNumber>
          <StatHelpText>By 2030 target</StatHelpText>
        </Stat>
      </SimpleGrid>

      {/* Latest Catalyst Alert */}
      <Alert status="info" bg="blue.50" border="1px solid" borderColor="blue.200" borderRadius="lg">
        <AlertIcon color="blue.500" />
        <Box>
          <AlertTitle color="blue.700">🚀 Latest RWA Catalyst</AlertTitle>
          <AlertDescription color="blue.600">
            {data.summary.topCatalyst}
          </AlertDescription>
        </Box>
      </Alert>

      {/* Horizon Filter Tabs */}
      <Tabs 
        colorScheme="purple" 
        variant="enclosed-colored"
        onChange={(index) => {
          const horizons = ['all', 'Daily', 'Weekly', 'Monthly', 'Long-Term'];
          setSelectedHorizon(horizons[index]);
        }}
      >
        <TabList>
          <Tab>🎯 All Signals</Tab>
          <Tab>⚡ Daily</Tab>
          <Tab>📈 Weekly</Tab>
          <Tab>📊 Monthly</Tab>
          <Tab>🚀 Long-Term</Tab>
        </TabList>

        <TabPanels>
          {/* All time horizons in the same format */}
          {['all', 'Daily', 'Weekly', 'Monthly', 'Long-Term'].map((horizon, idx) => (
            <TabPanel key={horizon} p={0} pt={6}>
              <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                {(horizon === 'all' ? data.signals : data.signals.filter(s => s.horizon === horizon))
                  .map((signal, index) => (
                  <Card
                    key={`${signal.asset}-${signal.horizon}-${index}`}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    _hover={{ 
                      shadow: 'lg', 
                      transform: 'translateY(-2px)',
                      borderColor: getHorizonColor(signal.horizon) + '.300'
                    }}
                    transition="all 0.2s"
                  >
                    <CardHeader pb={2}>
                      <HStack justify="space-between" align="start">
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Text fontWeight="bold" fontSize="xl" color="gray.800">
                              {signal.asset}
                            </Text>
                            <Badge
                              colorScheme={getSignalColor(signal.signal)}
                              px={3}
                              py={1}
                              fontSize="sm"
                              fontWeight="bold"
                            >
                              <HStack spacing={1}>
                                <Icon as={getSignalIcon(signal.signal)} boxSize={3} />
                                <Text>{signal.signal}</Text>
                              </HStack>
                            </Badge>
                          </HStack>
                          <Badge
                            colorScheme={getHorizonColor(signal.horizon)}
                            variant="subtle"
                            px={2}
                            py={1}
                            fontSize="xs"
                          >
                            {signal.horizon}
                          </Badge>
                        </VStack>

                        <VStack align="end" spacing={1}>
                          <Text fontSize="sm" color="gray.600" fontWeight="medium">
                            Confidence
                          </Text>
                          <Text fontSize="lg" fontWeight="bold" color="green.600">
                            {Math.round(signal.confidence)}%
                          </Text>
                          <Progress 
                            value={signal.confidence} 
                            colorScheme="green" 
                            size="sm" 
                            width="60px"
                            borderRadius="full"
                          />
                        </VStack>
                      </HStack>
                    </CardHeader>

                    <CardBody pt={0}>
                      <VStack spacing={4} align="stretch">
                        {/* Trade Parameters */}
                        <SimpleGrid columns={3} spacing={3}>
                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.600">
                              <HStack spacing={1}>
                                <Icon as={DollarSign} boxSize={3} />
                                <Text>Entry</Text>
                              </HStack>
                            </StatLabel>
                            <StatNumber fontSize="md" color="blue.600">
                              ${signal.entry}
                            </StatNumber>
                          </Stat>

                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.600">
                              <HStack spacing={1}>
                                <Icon as={Target} boxSize={3} />
                                <Text>Target</Text>
                              </HStack>
                            </StatLabel>
                            <StatNumber fontSize="md" color="green.600">
                              ${signal.target}
                            </StatNumber>
                          </Stat>

                          <Stat size="sm">
                            <StatLabel fontSize="xs" color="gray.600">
                              <HStack spacing={1}>
                                <Icon as={Shield} boxSize={3} />
                                <Text>Stop</Text>
                              </HStack>
                            </StatLabel>
                            <StatNumber fontSize="md" color="red.600">
                              ${signal.stopLoss}
                            </StatNumber>
                          </Stat>
                        </SimpleGrid>

                        <Divider />

                        {/* Performance Metrics */}
                        <SimpleGrid columns={2} spacing={4}>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="xs" color="gray.600" fontWeight="medium">
                              Risk/Reward Ratio
                            </Text>
                            <Text fontSize="lg" fontWeight="bold" color="purple.600">
                              {signal.riskReward}:1
                            </Text>
                          </VStack>

                          <VStack align="start" spacing={1}>
                            <Text fontSize="xs" color="gray.600" fontWeight="medium">
                              Expected Move
                            </Text>
                            <Text 
                              fontSize="lg" 
                              fontWeight="bold" 
                              color={signal.expectedMove > 0 ? 'green.600' : 'red.600'}
                            >
                              {signal.expectedMove > 0 ? '+' : ''}{signal.expectedMove}%
                            </Text>
                          </VStack>
                        </SimpleGrid>

                        {/* Long-term ROI for applicable signals */}
                        {signal.expectedROI && (
                          <Box bg="orange.50" p={3} borderRadius="md" border="1px solid" borderColor="orange.200">
                            <HStack justify="space-between">
                              <Text fontSize="xs" color="orange.700" fontWeight="medium">
                                Expected ROI:
                              </Text>
                              <Text fontSize="sm" fontWeight="bold" color="orange.700">
                                {signal.expectedROI}
                              </Text>
                            </HStack>
                          </Box>
                        )}

                        {/* Alpha Narrative */}
                        <Box bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                          <Text fontSize="sm" color="gray.700" lineHeight="1.4">
                            💡 <strong>Alpha:</strong> {signal.narrative}
                          </Text>
                        </Box>

                        {/* Execute Trade Button */}
                        {signal.confidence >= 85 && (
                          <Tooltip 
                            label={!walletState.isConnected ? "Connect MetaMask wallet to execute trades" : "Execute trade with connected wallet"}
                            hasArrow
                          >
                            <Button
                              leftIcon={<Zap />}
                              colorScheme={signal.signal === 'BUY' ? 'green' : 'red'}
                              variant="solid"
                              size="sm"
                              onClick={() => executeManualTrade(signal)}
                              isLoading={executingTrade === signal.asset}
                              loadingText="Executing..."
                              isDisabled={!walletState.isConnected}
                              width="full"
                            >
                              {walletState.isConnected 
                                ? `Execute ${signal.signal} Now` 
                                : `Connect Wallet to ${signal.signal}`}
                            </Button>
                          </Tooltip>
                        )}

                        {/* Bot Status Badge */}
                        {autoTradeConfig?.isEnabled && signal.confidence >= autoTradeConfig.confidenceThreshold && (
                          <Badge
                            colorScheme="green"
                            px={3}
                            py={1}
                            borderRadius="full"
                            fontSize="xs"
                            fontWeight="bold"
                          >
                            🤖 Bot Eligible
                          </Badge>
                        )}

                        {/* Timestamp */}
                        <HStack justify="center" spacing={1} pt={1}>
                          <Icon as={Clock} boxSize={3} color="gray.400" />
                          <Text fontSize="xs" color="gray.500">
                            Generated: {new Date(signal.timestamp).toLocaleString()}
                          </Text>
                        </HStack>
                      </VStack>
                    </CardBody>
                  </Card>
                ))}
              </SimpleGrid>

              {/* Show real catalyst data for each timeframe */}
              {horizon !== 'all' ? (() => {
                // Get catalysts for this timeframe (convert to lowercase to match API structure)
                const horizonKey = horizon.toLowerCase() === 'long-term' ? 'long-term' : horizon.toLowerCase();
                const timeframeCatalysts = data.catalysts?.[horizonKey] || [];
                
                return timeframeCatalysts.length > 0 ? (
                  <VStack spacing={4} align="stretch">
                    {timeframeCatalysts.map((catalyst, index) => (
                      <Box 
                        key={index}
                        bg={catalyst.impact === 'bullish' ? 'green.50' : catalyst.impact === 'bearish' ? 'red.50' : 'gray.50'}
                        p={4} 
                        borderRadius="lg" 
                        border="1px solid" 
                        borderColor={catalyst.impact === 'bullish' ? 'green.200' : catalyst.impact === 'bearish' ? 'red.200' : 'gray.200'}
                      >
                        <HStack justify="space-between" align="flex-start">
                          <VStack align="flex-start" spacing={2} flex={1}>
                            <HStack>
                              <Icon 
                                as={catalyst.impact === 'bullish' ? TrendingUp : catalyst.impact === 'bearish' ? TrendingDown : Activity} 
                                color={catalyst.impact === 'bullish' ? 'green.500' : catalyst.impact === 'bearish' ? 'red.500' : 'gray.500'} 
                                boxSize={4} 
                              />
                              <Text fontSize="md" fontWeight="semibold" color="gray.800">
                                {catalyst.title}
                              </Text>
                              <Badge 
                                colorScheme={catalyst.impact === 'bullish' ? 'green' : catalyst.impact === 'bearish' ? 'red' : 'gray'}
                                variant="subtle"
                              >
                                {catalyst.impact.toUpperCase()}
                              </Badge>
                            </HStack>
                            <Text fontSize="sm" color="gray.600" lineHeight="1.5">
                              {catalyst.description}
                            </Text>
                            <HStack spacing={4}>
                              <HStack>
                                <Icon as={Clock} boxSize={3} color="gray.400" />
                                <Text fontSize="xs" color="gray.500">{catalyst.timeframe}</Text>
                              </HStack>
                              <HStack>
                                <Icon as={Target} boxSize={3} color="gray.400" />
                                <Text fontSize="xs" color="gray.500">
                                  {catalyst.assets.join(', ')}
                                </Text>
                              </HStack>
                              <HStack>
                                <Icon as={ExternalLink} boxSize={3} color="gray.400" />
                                <Text fontSize="xs" color="gray.500">{catalyst.source}</Text>
                              </HStack>
                            </HStack>
                          </VStack>
                          <VStack align="flex-end" spacing={1}>
                            <Progress 
                              value={catalyst.confidence * 100} 
                              colorScheme={catalyst.confidence >= 0.8 ? 'green' : catalyst.confidence >= 0.6 ? 'yellow' : 'orange'}
                              size="sm" 
                              width="60px"
                            />
                            <Text fontSize="xs" color="gray.500">
                              {Math.round(catalyst.confidence * 100)}% confidence
                            </Text>
                          </VStack>
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box textAlign="center" py={8}>
                    <Icon as={Calendar} boxSize={16} color="gray.300" />
                    <Text mt={4} color="gray.500" fontSize="lg">
                      No {horizon} catalysts available
                    </Text>
                    <Text color="gray.400" fontSize="sm">
                      Check back later for updates
                    </Text>
                  </Box>
                );
              })() : null}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      {/* RWA Protocol TVL Stats */}
      <Card bg="white" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <HStack>
            <Icon as={Building2} color="blue.500" boxSize={5} />
            <Heading size="md" color="gray.800">Top RWA Protocols</Heading>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
            {(data.metrics?.topProtocols || []).map((protocol, index) => (
              <Stat key={protocol.name} size="sm">
                <StatLabel color="gray.600">{protocol.name}</StatLabel>
                <StatNumber color="blue.600">${protocol.tvl}</StatNumber>
                <StatHelpText color="green.500">
                  <HStack spacing={1}>
                    <Icon as={TrendingUp} boxSize={3} />
                    <Text>{protocol.change}</Text>
                  </HStack>
                </StatHelpText>
              </Stat>
            ))}
          </SimpleGrid>
        </CardBody>
      </Card>

      {/* Upcoming Catalysts */}
      <Card bg="white" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <HStack>
            <Icon as={BarChart3} color="orange.500" boxSize={5} />
            <Heading size="md" color="gray.800">Upcoming RWA Catalysts</Heading>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          <VStack spacing={3} align="stretch">
            {(() => {
              // Flatten all catalysts from all timeframes
              const allCatalysts = [];
              if (data.catalysts) {
                Object.values(data.catalysts).forEach((timeframeCatalysts) => {
                  if (Array.isArray(timeframeCatalysts)) {
                    allCatalysts.push(...timeframeCatalysts.slice(0, 2)); // Take 2 from each timeframe
                  }
                });
              }
              return allCatalysts.slice(0, 6).map((catalyst, index) => (
                <Box 
                  key={index} 
                  bg="orange.50" 
                  p={3} 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="orange.200"
                >
                  <HStack justify="space-between">
                    <HStack>
                      <Icon as={TrendingUp} color="orange.500" boxSize={4} />
                      <Text fontSize="sm" color="orange.800" fontWeight="medium">
                        {catalyst.title}
                      </Text>
                    </HStack>
                    <Badge colorScheme={catalyst.impact === 'bullish' ? 'green' : catalyst.impact === 'bearish' ? 'red' : 'gray'} size="sm">
                      {(catalyst.confidence * 100).toFixed(0)}% conf
                    </Badge>
                  </HStack>
                </Box>
              ));
            })()}
          </VStack>
        </CardBody>
      </Card>

      {/* Geopolitical Sentiment Index */}
      <GeopoliticalSentimentIndex />

      {/* Geopolitical Alert System (toggle with SHOW_ALERT_SYSTEM constant) */}
      {SHOW_ALERT_SYSTEM && <GeopoliticalAlertSystem />}

      {/* Wallet Connection */}
      <Card bg="white" border="1px solid" borderColor="gray.200">
        <CardHeader>
          <HStack>
            <Icon as={Wallet} color="blue.500" boxSize={5} />
            <Heading size="md" color="gray.800">Wallet Connection</Heading>
          </HStack>
        </CardHeader>
        <CardBody pt={0}>
          {!walletState.isConnected ? (
            <Button
              leftIcon={<Icon as={Wallet} />}
              colorScheme="blue"
              size="lg"
              width="full"
              onClick={connectWallet}
            >
              Connect MetaMask Wallet
            </Button>
          ) : (
            <VStack spacing={3} align="stretch">
              <Box p={4} bg="green.50" borderRadius="lg" borderWidth="2px" borderColor="green.200">
                <HStack justify="space-between" mb={2}>
                  <Text fontSize="sm" fontWeight="bold" color="green.700">
                    ✅ Wallet Connected
                  </Text>
                  <Button size="xs" variant="ghost" onClick={disconnectWallet}>
                    Disconnect
                  </Button>
                </HStack>
                <Text fontSize="sm" fontFamily="mono" color="gray.700">
                  {walletState.address.substring(0, 6)}...{walletState.address.substring(38)}
                </Text>
                <HStack justify="space-between" mt={2}>
                  <Text fontSize="sm" color="gray.600">Balance:</Text>
                  <Text fontSize="md" fontWeight="bold" color="green.600">
                    {walletState.balance} ETH
                  </Text>
                </HStack>
              </Box>
              <Alert status="info" variant="left-accent">
                <AlertIcon />
                <Text fontSize="sm">
                  Wallet connected for automatic RWA trade execution based on your approval settings.
                </Text>
              </Alert>
            </VStack>
          )}
        </CardBody>
      </Card>

      {/* Footer Note */}
      <Box textAlign="center" pt={4}>
        <Text fontSize="xs" color="gray.500">
          🔄 Auto-refresh every {Math.round(refreshInterval / 60000)} minutes | 
          💎 RWA signals powered by multi-horizon AI analysis | 
          ⚠️ Not financial advice
        </Text>
      </Box>

      {/* Auto-Trade Settings Modal */}
      <Modal isOpen={isMainModalOpen} onClose={onMainModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={Settings} color="purple.500" />
              <Text>Auto-Trade Bot Settings</Text>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6}>
              {/* Portfolio Configuration */}
              <FormControl>
                <FormLabel>Portfolio Size (USD)</FormLabel>
                <NumberInput
                  value={autoTradeConfig?.portfolioSize || 10000}
                  onChange={(_, value) => updateAutoTradeConfig({ portfolioSize: value })}
                  min={1000}
                  max={1000000}
                  step={1000}
                >
                  <NumberInputField placeholder="10000" />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              {/* Confidence Threshold */}
              <FormControl>
                <FormLabel>Minimum Confidence Threshold (%)</FormLabel>
                <NumberInput
                  value={autoTradeConfig?.confidenceThreshold || 85}
                  onChange={(_, value) => updateAutoTradeConfig({ confidenceThreshold: value })}
                  min={75}
                  max={95}
                  step={5}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              {/* Max Trades Per Day */}
              <FormControl>
                <FormLabel>Max Trades Per Day</FormLabel>
                <NumberInput
                  value={autoTradeConfig?.maxTradesPerDay || 3}
                  onChange={(_, value) => updateAutoTradeConfig({ maxTradesPerDay: value })}
                  min={1}
                  max={10}
                  step={1}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              {/* Current Status */}
              {autoTradeConfig && (
                <VStack spacing={3} align="stretch" width="full">
                  <Text fontWeight="bold" color="gray.700">Current Status:</Text>
                  
                  <SimpleGrid columns={2} spacing={3}>
                    <Stat size="sm">
                      <StatLabel>Active Positions</StatLabel>
                      <StatNumber color="blue.600">
                        {autoTradeConfig.activePositions?.length || 0}
                      </StatNumber>
                    </Stat>
                    
                    <Stat size="sm">
                      <StatLabel>Trades Today</StatLabel>
                      <StatNumber color="green.600">
                        {autoTradeConfig.dailyTradeCount}/{autoTradeConfig.maxTradesPerDay}
                      </StatNumber>
                    </Stat>
                  </SimpleGrid>

                  {/* Recent Trades */}
                  {autoTradeConfig.tradeHistory && autoTradeConfig.tradeHistory.length > 0 && (
                    <Box>
                      <Text fontWeight="bold" color="gray.700" mb={2}>Recent Trades:</Text>
                      <VStack spacing={2} align="stretch" maxH="200px" overflowY="auto">
                        {autoTradeConfig.tradeHistory.slice(0, 5).map((trade, index) => (
                          <Box key={index} bg="gray.50" p={2} borderRadius="md" fontSize="sm">
                            <HStack justify="space-between">
                              <Text fontWeight="medium">{trade.asset} {trade.signal}</Text>
                              <Text color="gray.600">{trade.confidence}% confidence</Text>
                            </HStack>
                            <Text fontSize="xs" color="gray.500">
                              {new Date(trade.timestamp).toLocaleString()}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  )}
                </VStack>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="purple" onClick={onMainModalClose}>
              Save Settings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Enhanced Backtest Modal */}
      <Modal isOpen={isBacktestOpen} onClose={onBacktestClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <HStack>
              <Icon as={BarChart3} color="blue.500" boxSize={6} />
              <Text>Advanced RWA Strategy Backtesting</Text>
              <Badge colorScheme="purple" variant="subtle">Enhanced Feb 2026</Badge>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={6} align="stretch">
              {/* Enhanced Backtest Parameters */}
              <Card>
                <CardHeader>
                  <HStack justify="space-between">
                    <Heading size="sm">Strategy Configuration</Heading>
                    <Badge colorScheme="green">$24B+ RWA TVL</Badge>
                  </HStack>
                </CardHeader>
                <CardBody>
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                    {/* Asset Selection */}
                    <FormControl>
                      <FormLabel>RWA Assets & Crypto</FormLabel>
                      <Input
                        value={backtestParams.assets.join(', ')}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          assets: e.target.value.split(',').map(s => s.trim())
                        })}
                        placeholder="ONDO, CFG, VBILL, BLK, ETH"
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        NEW: ONDO (Tokenized Yield), CFG (Credit), VBILL (Treasury)
                      </Text>
                    </FormControl>

                    {/* Strategy Type */}
                    <FormControl>
                      <FormLabel>Strategy Type</FormLabel>
                      <Select
                        value={backtestParams.strategy_type || 'sma_momentum'}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          strategy_type: e.target.value
                        })}
                      >
                        <option value="sma_momentum">SMA Momentum (Default)</option>
                        <option value="sma_volume_filtered">SMA + Volume Filter</option>
                        <option value="rsi_strategy">RSI Strategy</option>
                        <option value="rwa_catalyst">RWA Catalyst Events</option>
                        <option value="rwa_weighted">RWA Leaders Weighted</option>
                      </Select>
                    </FormControl>

                    {/* Benchmark */}
                    <FormControl>
                      <FormLabel>Benchmark</FormLabel>
                      <Select
                        value={backtestParams.benchmark || 'SPY'}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          benchmark: e.target.value
                        })}
                      >
                        <option value="SPY">S&P 500 (SPY)</option>
                        <option value="BLK">BlackRock (BLK)</option>
                        <option value="ETH">Ethereum (ETH)</option>
                        <option value="VTI">Total Market (VTI)</option>
                      </Select>
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>Start Date</FormLabel>
                      <Input
                        type="date"
                        value={backtestParams.start_date}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          start_date: e.target.value
                        })}
                      />
                    </FormControl>
                    
                    <FormControl>
                      <FormLabel>End Date</FormLabel>
                      <Input
                        type="date"
                        value={backtestParams.end_date}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          end_date: e.target.value
                        })}
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Initial Capital ($)</FormLabel>
                      <NumberInput
                        value={backtestParams.initial_capital || 10000}
                        onChange={(_, value) => setBacktestParams({
                          ...backtestParams,
                          initial_capital: value || 10000
                        })}
                        min={1000}
                        max={1000000}
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                    </FormControl>
                  </SimpleGrid>

                  {/* Strategy-Specific Parameters */}
                  <Box mt={4} p={4} bg="gray.50" borderRadius="lg">
                    <Text fontWeight="semibold" mb={3}>Strategy Parameters</Text>
                    <SimpleGrid columns={{ base: 1, md: 4 }} spacing={3}>
                      <FormControl>
                        <FormLabel fontSize="sm">SMA Short</FormLabel>
                        <NumberInput
                          value={backtestParams.sma_short || 10}
                          onChange={(_, value) => setBacktestParams({
                            ...backtestParams,
                            sma_short: value || 10
                          })}
                          min={5}
                          max={50}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">SMA Long</FormLabel>
                        <NumberInput
                          value={backtestParams.sma_long || 50}
                          onChange={(_, value) => setBacktestParams({
                            ...backtestParams,
                            sma_long: value || 50
                          })}
                          min={20}
                          max={200}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Signal Threshold</FormLabel>
                        <NumberInput
                          value={backtestParams.high_conf_threshold || 2.0}
                          onChange={(_, value) => setBacktestParams({
                            ...backtestParams,
                            high_conf_threshold: value || 2.0
                          })}
                          min={0.5}
                          max={10}
                          step={0.1}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm">Volume Filter</FormLabel>
                        <NumberInput
                          value={backtestParams.volume_filter || 1.5}
                          onChange={(_, value) => setBacktestParams({
                            ...backtestParams,
                            volume_filter: value || 1.5
                          })}
                          min={1.0}
                          max={3.0}
                          step={0.1}
                        >
                          <NumberInputField />
                        </NumberInput>
                      </FormControl>
                    </SimpleGrid>
                  </Box>

                  {/* Advanced Options */}
                  <Box mt={4} p={4} bg="blue.50" borderRadius="lg">
                    <Text fontWeight="semibold" mb={3}>Advanced Testing</Text>
                    <HStack spacing={6}>
                      <Checkbox
                        isChecked={backtestParams.monte_carlo || false}
                        onChange={(e) => setBacktestParams({
                          ...backtestParams,
                          monte_carlo: e.target.checked
                        })}
                      >
                        Monte Carlo Simulation
                      </Checkbox>
                      
                      {backtestParams.monte_carlo && (
                        <FormControl maxW="150px">
                          <FormLabel fontSize="sm">Simulations</FormLabel>
                          <NumberInput
                            value={backtestParams.monte_carlo_sims || 100}
                            onChange={(_, value) => setBacktestParams({
                              ...backtestParams,
                              monte_carlo_sims: value || 100
                            })}
                            min={10}
                            max={500}
                          >
                            <NumberInputField />
                          </NumberInput>
                        </FormControl>
                      )}
                    </HStack>
                  </Box>
                  
                  <Button
                    leftIcon={<Play size={16} />}
                    colorScheme="blue"
                    onClick={runBacktest}
                    isLoading={backtestLoading}
                    loadingText="Running Enhanced Backtest..."
                    mt={4}
                    width="full"
                    size="lg"
                  >
                    Run Advanced RWA Backtest
                  </Button>
                </CardBody>
              </Card>

              {/* Enhanced Backtest Results */}
              {backtestResult && (
                <>
                  {/* Performance Metrics Dashboard */}
                  <Card>
                    <CardHeader>
                      <HStack justify="space-between">
                        <VStack align="start" spacing={1}>
                          <Heading size="sm">Performance Dashboard</Heading>
                          <HStack>
                            <Badge colorScheme="blue">{backtestResult.summary?.strategy || 'SMA Strategy'}</Badge>
                            <Badge colorScheme="green">{(backtestResult as any)?.context?.rwa_market_size || '$24B+ TVL'}</Badge>
                          </HStack>
                        </VStack>
                        <Button
                          leftIcon={<Download size={16} />}
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const csvData = backtestResult.equity_curve.map(row => ({
                              Date: row.date,
                              'Portfolio Value': row.value,
                              'High Conf Value': row.high_conf_value,
                              'Benchmark Value': (row as any)?.benchmark_value || 1.0,
                              'Drawdown': (row as any)?.drawdown || 0
                            }));
                            
                            const csv = [
                              Object.keys(csvData[0]).join(','),
                              ...csvData.map(row => Object.values(row).join(','))
                            ].join('\n');
                            
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `rwa_backtest_${new Date().toISOString().split('T')[0]}.csv`;
                            a.click();
                          }}
                        >
                          Export CSV
                        </Button>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} spacing={4}>
                        <Stat>
                          <StatLabel>Total Return</StatLabel>
                          <StatNumber color={backtestResult.metrics?.total_return >= 0 ? 'green.500' : 'red.500'}>
                            {formatPercentage(backtestResult.metrics?.total_return || 0)}
                          </StatNumber>
                          <StatHelpText>vs {formatPercentage((backtestResult.metrics as any)?.benchmark_return || 0)} benchmark</StatHelpText>
                        </Stat>
                        
                        <Stat>
                          <StatLabel>Alpha</StatLabel>
                          <StatNumber color={((backtestResult.metrics as any)?.alpha || 0) >= 0 ? 'green.500' : 'red.500'}>
                            {formatPercentage((backtestResult.metrics as any)?.alpha || 0)}
                          </StatNumber>
                          <StatHelpText>Excess return</StatHelpText>
                        </Stat>
                        
                        <Stat>
                          <StatLabel>Max Drawdown</StatLabel>
                          <StatNumber color="red.500">
                            -{formatPercentage(backtestResult.metrics?.max_drawdown || 0)}
                          </StatNumber>
                          <StatHelpText>Peak to Trough</StatHelpText>
                        </Stat>
                        
                        <Stat>
                          <StatLabel>Sharpe Ratio</StatLabel>
                          <StatNumber color={(backtestResult.metrics?.sharpe_ratio || 0) >= 1 ? 'green.500' : 'yellow.500'}>
                            {(backtestResult.metrics?.sharpe_ratio || 0).toFixed(2)}
                          </StatNumber>
                          <StatHelpText>Risk-Adjusted</StatHelpText>
                        </Stat>
                        
                        <Stat>
                          <StatLabel>Win Rate</StatLabel>
                          <StatNumber color={(backtestResult.metrics?.win_rate || 0) >= 0.5 ? 'green.500' : 'red.500'}>
                            {formatPercentage(backtestResult.metrics?.win_rate || 0)}
                          </StatNumber>
                          <StatHelpText>{backtestResult.metrics?.total_trades || 0} Trades</StatHelpText>
                        </Stat>

                        <Stat>
                          <StatLabel>Volatility</StatLabel>
                          <StatNumber>
                            {formatPercentage((backtestResult.metrics as any)?.volatility || 0)}
                          </StatNumber>
                          <StatHelpText>Annualized</StatHelpText>
                        </Stat>
                      </SimpleGrid>

                      {/* High Confidence vs All Signals Comparison */}
                      {backtestResult.metrics?.high_conf_total_return !== undefined && (
                        <Box mt={6} p={4} bg="blue.50" borderRadius="lg">
                          <Text fontWeight="semibold" mb={3}>Signal Filtering Impact</Text>
                          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                            <Stat size="sm">
                              <StatLabel>High Conf Return</StatLabel>
                              <StatNumber color={(backtestResult.metrics.high_conf_total_return || 0) >= 0 ? 'green.500' : 'red.500'}>
                                {formatPercentage(backtestResult.metrics.high_conf_total_return || 0)}
                              </StatNumber>
                              <StatHelpText>{backtestResult.metrics.high_conf_trades || 0} signals</StatHelpText>
                            </Stat>
                            
                            <Stat size="sm">
                              <StatLabel>High Conf Sharpe</StatLabel>
                              <StatNumber color={(backtestResult.metrics.high_conf_sharpe_ratio || 0) >= 1 ? 'green.500' : 'yellow.500'}>
                                {(backtestResult.metrics.high_conf_sharpe_ratio || 0).toFixed(2)}
                              </StatNumber>
                              <StatHelpText>Filtered strategy</StatHelpText>
                            </Stat>

                            <Stat size="sm">
                              <StatLabel>High Conf Drawdown</StatLabel>
                              <StatNumber color="red.500">
                                -{formatPercentage(backtestResult.metrics.high_conf_max_drawdown || 0)}
                              </StatNumber>
                              <StatHelpText>Risk reduction</StatHelpText>
                            </Stat>

                            <Stat size="sm">
                              <StatLabel>High Conf Win Rate</StatLabel>
                              <StatNumber color={(backtestResult.metrics.high_conf_win_rate || 0) >= 0.5 ? 'green.500' : 'red.500'}>
                                {formatPercentage(backtestResult.metrics.high_conf_win_rate || 0)}
                              </StatNumber>
                              <StatHelpText>Quality improvement</StatHelpText>
                            </Stat>
                          </SimpleGrid>
                        </Box>
                      )}

                      {/* Monte Carlo Results */}
                      {(backtestResult as any).monte_carlo && (
                        <Box mt={6} p={4} bg="purple.50" borderRadius="lg">
                          <Text fontWeight="semibold" mb={3}>Monte Carlo Analysis ({(backtestResult as any).monte_carlo.simulations} runs)</Text>
                          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                            <Stat size="sm">
                              <StatLabel>Expected Return</StatLabel>
                              <StatNumber>
                                {formatPercentage((backtestResult as any).monte_carlo.mean_return)}
                              </StatNumber>
                              <StatHelpText>Average outcome</StatHelpText>
                            </Stat>
                            
                            <Stat size="sm">
                              <StatLabel>Best Case (95%)</StatLabel>
                              <StatNumber color="green.500">
                                {formatPercentage((backtestResult as any).monte_carlo.percentile_95)}
                              </StatNumber>
                              <StatHelpText>Top 5% scenarios</StatHelpText>
                            </Stat>

                            <Stat size="sm">
                              <StatLabel>Worst Case (5%)</StatLabel>
                              <StatNumber color="red.500">
                                {formatPercentage((backtestResult as any).monte_carlo.percentile_5)}
                              </StatNumber>
                              <StatHelpText>Bottom 5% scenarios</StatHelpText>
                            </Stat>

                            <Stat size="sm">
                              <StatLabel>Win Probability</StatLabel>
                              <StatNumber color={((backtestResult as any).monte_carlo.win_probability || 0) >= 0.6 ? 'green.500' : 'yellow.500'}>
                                {formatPercentage((backtestResult as any).monte_carlo.win_probability)}
                              </StatNumber>
                              <StatHelpText>Positive return</StatHelpText>
                            </Stat>
                          </SimpleGrid>
                        </Box>
                      )}
                    </CardBody>
                  </Card>

                  {/* Enhanced Charts */}
                  <Card>
                    <CardHeader>
                      <HStack justify="space-between">
                        <Heading size="sm">Performance Analysis</Heading>
                        <HStack>
                          <Badge colorScheme="blue">Log Scale</Badge>
                          <Badge colorScheme="green">Benchmark</Badge>
                          <Badge colorScheme="purple">Drawdown</Badge>
                        </HStack>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <Box height="500px">
                        <ResponsiveContainer width="100%" height="100%">
                          <ComposedChart data={backtestResult.equity_curve}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 11 }}
                            />
                            <YAxis 
                              yAxisId="left"
                              tick={{ fontSize: 11 }}
                              domain={['dataMin', 'dataMax']}
                              scale="log"
                            />
                            <YAxis 
                              yAxisId="right"
                              orientation="right"
                              tick={{ fontSize: 11 }}
                              domain={[-1, 0]}
                            />
                            <RechartsTooltip 
                              labelFormatter={(value) => `Date: ${value}`}
                              formatter={(value, name) => {
                                if (name === 'drawdown') return [`-${formatPercentage(Math.abs(Number(value)))}`, 'Drawdown'];
                                return [`${(value as number).toFixed(4)}x`, 
                                        name === 'value' ? 'Portfolio' : 
                                        name === 'high_conf_value' ? 'High Confidence' : 'Benchmark'];
                              }}
                            />
                            <Legend />
                            
                            {/* Benchmark Line */}
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="benchmark_value"
                              stroke="#94A3B8"
                              strokeWidth={2}
                              dot={false}
                              name="Benchmark"
                            />
                            
                            {/* Portfolio Performance */}
                            <Area
                              yAxisId="left"
                              type="monotone"
                              dataKey="value"
                              stackId="1"
                              stroke="#3B82F6"
                              fill="#3B82F6"
                              fillOpacity={0.3}
                              name="Portfolio"
                            />
                            
                            {/* High Confidence Strategy */}
                            {backtestResult.equity_curve.some(d => d.high_conf_value !== undefined) && (
                              <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="high_conf_value"
                                stackId="2"
                                stroke="#10B981"
                                fill="#10B981"
                                fillOpacity={0.3}
                                name="High Confidence"
                              />
                            )}

                            {/* Drawdown Area */}
                            <Area
                              yAxisId="right"
                              type="monotone"
                              dataKey="drawdown"
                              stroke="#EF4444"
                              fill="#EF4444"
                              fillOpacity={0.2}
                              name="Drawdown"
                            />

                            {/* Event Lines for RWA Catalysts */}
                            {(backtestResult as any).events && (backtestResult as any).events.map((event: any, idx: number) => (
                              <ReferenceLine 
                                key={idx}
                                x={event.date} 
                                stroke="#A855F7" 
                                strokeDasharray="5 5"
                                label={{ value: event.name, angle: -90, position: 'top' }}
                              />
                            ))}
                          </ComposedChart>
                        </ResponsiveContainer>
                      </Box>
                    </CardBody>
                  </Card>
                  
                  {/* Enhanced Asset Performance with RWA Context */}
                  <Card>
                    <CardHeader>
                      <HStack justify="space-between">
                        <Heading size="sm">Individual Asset Performance</Heading>
                        <VStack align="end" spacing={1}>
                          <Text fontSize="xs" color="gray.500">RWA Leaders as of Feb 3, 2026</Text>
                          <HStack spacing={2}>
                            <Badge colorScheme="green" variant="subtle">Ondo $1.5B+</Badge>
                            <Badge colorScheme="blue" variant="subtle">BUIDL $2.9B</Badge>
                            <Badge colorScheme="purple" variant="subtle">+14% Growth</Badge>
                          </HStack>
                        </VStack>
                      </HStack>
                    </CardHeader>
                    <CardBody>
                      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                        {Object.entries(backtestResult.asset_performance || {}).map(([asset, performance]) => (
                          <Box key={asset} p={4} borderRadius="lg" border="1px solid" borderColor="gray.200" 
                               bg={['ONDO', 'CFG', 'VBILL'].includes(asset) ? 'purple.50' : 'gray.50'}>
                            <VStack spacing={3} align="start">
                              <HStack justify="space-between" w="full">
                                <VStack align="start" spacing={1}>
                                  <HStack>
                                    <Badge colorScheme={
                                      ['ONDO', 'CFG', 'VBILL'].includes(asset) ? 'purple' : 
                                      ['ETH', 'HBAR', 'USDC'].includes(asset) ? 'cyan' : 'blue'
                                    }>
                                      {asset}
                                    </Badge>
                                    <Badge colorScheme={performance.current_signal === 'BUY' ? 'green' : 'yellow'} size="sm">
                                      {performance.current_signal}
                                    </Badge>
                                  </HStack>
                                  <Text fontSize="xs" color="gray.600">
                                    {asset === 'ONDO' ? 'Tokenized Yield Leader' :
                                     asset === 'CFG' ? 'Credit Tokenization' :
                                     asset === 'VBILL' ? 'VanEck Treasury Bills' :
                                     asset === 'BLK' ? 'BlackRock ETF Giant' :
                                     asset === 'VDR' ? 'Vanguard Digital Reserve' :
                                     asset === 'ETH' ? 'DeFi Infrastructure' :
                                     asset === 'HBAR' ? 'Enterprise Blockchain' : 
                                     'Digital Asset'}
                                  </Text>
                                </VStack>
                              </HStack>

                              <HStack justify="space-between" w="full">
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="lg" fontWeight="bold" color={performance.total_return >= 0 ? 'green.500' : 'red.500'}>
                                    {formatPercentage(performance.total_return)}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">Total Return</Text>
                                </VStack>
                                
                                <VStack align="end" spacing={1}>
                                  <Text fontSize="sm" color="gray.600">
                                    {formatPercentage(performance.volatility)}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">Volatility</Text>
                                </VStack>
                              </HStack>

                              {performance.sharpe_ratio && (
                                <HStack justify="space-between" w="full">
                                  <Text fontSize="sm" color="gray.600">
                                    Sharpe: {performance.sharpe_ratio.toFixed(2)}
                                  </Text>
                                  <Text fontSize="sm" color="gray.600">
                                    Trades: {performance.trade_count || 0}
                                  </Text>
                                </HStack>
                              )}

                              {/* RWA-Specific Insights */}
                              {asset === 'ONDO' && (
                                <Text fontSize="xs" color="purple.600" fontStyle="italic">
                                  🏆 Tokenization category leader
                                </Text>
                              )}
                              {asset === 'CFG' && (
                                <Text fontSize="xs" color="purple.600" fontStyle="italic">
                                  🌐 Real-world credit on-chain
                                </Text>
                              )}
                              {asset === 'VBILL' && (
                                <Text fontSize="xs" color="purple.600" fontStyle="italic">
                                  🛡️ Treasury bill tokenization
                                </Text>
                              )}
                            </VStack>
                          </Box>
                        ))}
                      </SimpleGrid>

                      {/* RWA Market Context */}
                      <Box mt={6} p={4} bg="gradient.500" bgGradient="linear(to-r, purple.50, blue.50)" borderRadius="lg">
                        <VStack spacing={3} align="start">
                          <HStack>
                            <Icon as={TrendingUp} color="purple.500" />
                            <Text fontWeight="semibold" color="purple.800">Real-World Asset Market Context</Text>
                          </HStack>
                          
                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} w="full">
                            <Box>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.700">Market Leaders</Text>
                              <VStack align="start" spacing={1} mt={1}>
                                <Text fontSize="xs" color="gray.600">• Ondo Finance: $1.5B+ USDY TVL</Text>
                                <Text fontSize="xs" color="gray.600">• BlackRock BUIDL: $2.9B Treasury</Text>
                                <Text fontSize="xs" color="gray.600">• Franklin BENJI: $890M</Text>
                              </VStack>
                            </Box>
                            
                            <Box>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.700">Growth Metrics</Text>
                              <VStack align="start" spacing={1} mt={1}>
                                <Text fontSize="xs" color="gray.600">• Total RWA TVL: $24B+</Text>
                                <Text fontSize="xs" color="gray.600">• 30-day growth: +14%</Text>
                                <Text fontSize="xs" color="gray.600">• New protocols: 12+ launched</Text>
                              </VStack>
                            </Box>
                            
                            <Box>
                              <Text fontSize="sm" fontWeight="semibold" color="gray.700">Key Trends</Text>
                              <VStack align="start" spacing={1} mt={1}>
                                <Text fontSize="xs" color="gray.600">• Tokenized Treasury dominance</Text>
                                <Text fontSize="xs" color="gray.600">• Credit market expansion</Text>
                                <Text fontSize="xs" color="gray.600">• Institutional adoption</Text>
                              </VStack>
                            </Box>
                          </SimpleGrid>
                          
                          <Text fontSize="xs" color="gray.500" fontStyle="italic" mt={2}>
                            Source: rwa.xyz market data • Last updated: Feb 3, 2026
                          </Text>
                        </VStack>
                      </Box>

                      {/* Strategy Notes & Disclaimers */}
                      <Box mt={4} p={4} bg="orange.50" borderRadius="lg" borderLeft="4px solid orange.400">
                        <VStack align="start" spacing={2}>
                          <HStack>
                            <Icon as={AlertTriangle} color="orange.500" />
                            <Text fontWeight="semibold" color="orange.800">Strategy Notes & Disclaimers</Text>
                          </HStack>
                          
                          {(backtestResult as any).strategy_notes && (
                            <Text fontSize="sm" color="orange.700">
                              <strong>Strategy:</strong> {(backtestResult as any).strategy_notes}
                            </Text>
                          )}
                          
                          <Text fontSize="sm" color="orange.700">
                            <strong>Important:</strong> {(backtestResult as any).disclaimer || 'Backtest uses historical proxy strategy; not predictive. RWA markets are emerging and volatile.'}
                          </Text>
                          
                          <Text fontSize="xs" color="orange.600">
                            Past performance does not guarantee future results. RWA tokenization is a rapidly evolving space with regulatory, technical, and market risks. This analysis is for educational purposes only.
                          </Text>
                        </VStack>
                      </Box>
                    </CardBody>
                  </Card>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={onBacktestClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Knowledge Base */}
      <KnowledgeBase 
        isOpen={isKnowledgeBaseOpen} 
        onClose={onKnowledgeBaseClose} 
      />
      
      {/* Onboarding Tutorial */}
      <OnboardingTutorial 
        isOpen={isTutorialOpen} 
        onClose={onTutorialClose}
        onComplete={handleTutorialComplete}
      />
      
      {/* Help Center - Always visible */}
      <HelpCenter 
        currentPage="rwa-playbook"
        onOpenKnowledgeBase={onKnowledgeBaseOpen}
        onOpenTutorial={onTutorialOpen}
      />
    </VStack>
  );
};

export default RWAPlaybook;